/**
 * Date/Month/Range Picker 문자열 변환.
 * - 일자: yyyy-MM-dd (API·폼 값)
 * - 년월: yyyyMM (API) / 표시용 YYYY.MM
 */
import { format, isValid, parse, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { DISPLAY_EMPTY, formatYearMonth } from './dateFormat';

export const DATE_VALUE_FORMAT = 'yyyy-MM-dd';

export const parseDateValue = (value?: string | null): Date | undefined => {
  if (!value || !String(value).trim()) return undefined;
  const raw = String(value).trim();
  const iso = parseISO(raw);
  if (isValid(iso) && raw.includes('-') && raw.length >= 10) return iso;
  const parsed = parse(raw, DATE_VALUE_FORMAT, new Date());
  return isValid(parsed) ? parsed : undefined;
};

export const formatDateValue = (date?: Date): string => {
  if (!date || !isValid(date)) return '';
  return format(date, DATE_VALUE_FORMAT);
};

export const formatDateDisplay = (value?: string | null): string => {
  const date = parseDateValue(value);
  if (!date) return '';
  return format(date, 'yyyy.MM.dd', { locale: ko });
};

/** API yyyyMM → Date(해당 월 1일) */
export const parseRefYmValue = (value?: string | null): Date | undefined => {
  if (!value) return undefined;
  const digits = String(value).replace(/\D/g, '');
  if (digits.length < 6) return undefined;
  const parsed = parse(digits.slice(0, 6), 'yyyyMM', new Date());
  return isValid(parsed) ? parsed : undefined;
};

export const formatRefYmValue = (date?: Date): string => {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyyMM');
};

export const formatRefYmDisplay = (value?: string | null): string => {
  const formatted = formatYearMonth(value);
  return formatted === DISPLAY_EMPTY ? '' : formatted;
};

export const parseDateRangeValue = (range?: {
  from?: string;
  to?: string;
}): DateRange | undefined => {
  const from = parseDateValue(range?.from);
  const to = parseDateValue(range?.to);
  if (!from && !to) return undefined;
  return { from, to };
};

export const formatDateRangeValue = (
  range?: DateRange,
): { from: string; to: string } => ({
  from: formatDateValue(range?.from),
  to: formatDateValue(range?.to),
});

export const formatDateRangeDisplay = (range?: {
  from?: string;
  to?: string;
}): string => {
  const from = formatDateDisplay(range?.from);
  const to = formatDateDisplay(range?.to);
  if (from && to) return `${from} ~ ${to}`;
  if (from) return from;
  return '';
};

/**
 * react-day-picker range 모드: 첫 클릭에 from===to 를 채운다.
 * 같은 날이면 “종료일 선택 대기”로 보고, 두 번째 클릭(또는 다른 날)에서만 완료로 본다.
 */
export const isDateRangeSelectionComplete = (
  range: DateRange | undefined,
  awaitingEnd: boolean,
): boolean => {
  if (!range?.from || !range?.to) return false;
  if (formatDateValue(range.from) !== formatDateValue(range.to)) return true;
  return awaitingEnd;
};

/** datetime-local (`yyyy-MM-ddTHH:mm`) 조각 */
export const splitDateTimeLocalValue = (
  value?: string | null,
): { date: string; time: string } => {
  if (!value) return { date: '', time: '' };
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const [date = '', timePart = ''] = normalized.split('T');
  return { date, time: timePart.slice(0, 5) };
};

export const composeDateTimeLocalValue = (
  date: string,
  time: string,
): string => {
  if (!date) return '';
  return `${date}T${time || '00:00'}`;
};
